// Dashboard: lokasyona göre ilk 10 iş tipi/çeşidi ve İSG onay süresi dağılımı
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Briefcase, Timer } from 'lucide-react';
import { ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from 'recharts';
import { PERIODS } from './PermitWidgets';

const COLORS = ['#f97316', '#fb923c', '#fdba74', '#0ea5e9', '#38bdf8', '#7dd3fc', '#10b981', '#34d399', '#a3e635', '#94a3b8'];
const BUCKETS = [['< 1 sa', 60], ['1–4 sa', 240], ['4–24 sa', 1440], ['1–3 gün', 4320], ['> 3 gün', Infinity]];
const BUCKET_COLORS = ['#10b981', '#84cc16', '#f59e0b', '#f97316', '#ef4444'];

export const fmtMinutes = (m) => {
  if (m < 60) return `${Math.round(m)} dk`;
  if (m < 1440) return `${Math.floor(m / 60)} sa ${Math.round(m % 60)} dk`;
  return `${Math.floor(m / 1440)} gün ${Math.floor((m % 1440) / 60)} sa`;
};

const LocationSelect = ({ value, onChange, locations, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-8 w-52 text-sm font-normal" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">Tüm Lokasyonlar</SelectItem>
      {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Empty = ({ icon: Icon, text }) => (
  <div className="flex h-72 items-center justify-center text-slate-400"><div className="text-center"><Icon className="mx-auto mb-2 h-12 w-12 opacity-50" /><p className="text-sm">{text}</p></div></div>
);

const HBar = ({ data, width, tip, colors }) => (
  <div className="h-72">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" accessibilityLayer={false} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" hide allowDecimals={false} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={width} interval={0} />
        <Tooltip cursor={false} separator="" formatter={(v) => [tip(v), '']} />
        <Bar dataKey="value" maxBarSize={28} radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 12, fill: '#475569' }}>
          {data.map((d, i) => <Cell key={d.name} fill={colors[i % colors.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export const TopJobTypesChart = ({ jobTypes, locations }) => {
  const [loc, setLoc] = useState('all');
  const data = (jobTypes[loc] || []).map(([name, value]) => ({ name, value }));
  return (
    <Card className="border-slate-200" data-testid="dashboard-top-jobtypes">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center justify-between gap-3 text-lg">
          <span className="flex items-center gap-2"><Briefcase className="h-5 w-5" />En Çok Talep Gelen İş Çeşitleri <span className="text-sm font-normal text-slate-400">(ilk 10)</span></span>
          <LocationSelect value={loc} onChange={setLoc} locations={locations} testid="dashboard-jobtypes-location" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data.length ? <HBar data={data} width={220} tip={v => `${v} talep`} colors={COLORS} /> : <Empty icon={Briefcase} text="Bu lokasyonda talep yok" />}
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg bg-slate-50 px-4 py-2" data-testid={testid}><p className="text-xs text-slate-500">{label}</p><p className="text-lg font-bold text-slate-900">{value}</p></div>
);

export const ApprovalTimeChart = ({ stats, locations }) => {
  const [loc, setLoc] = useState('all');
  const [period, setPeriod] = useState('all');
  // Sunucuda özetlenir: adet, ortalama, ortanca ve süre dilimleri
  const st = stats[`${loc}|${period}`] || { count: 0, avg: 0, median: 0, buckets: [] };
  const list = { length: st.count };
  const { avg, median } = st;
  const data = BUCKETS.map(([name], i) => ({ name, value: st.buckets[i] || 0 }));
  return (
    <Card className="border-slate-200" data-testid="dashboard-approval-times">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center justify-between gap-3 text-lg">
          <span className="flex items-center gap-2"><Timer className="h-5 w-5" />İSG Onay Süreleri <span className="text-sm font-normal text-slate-400" data-testid="dashboard-approval-count">({list.length} talep)</span></span>
          <span className="flex flex-wrap items-center gap-3">
            <LocationSelect value={loc} onChange={setLoc} locations={locations} testid="dashboard-approval-location" />
            <span className="flex rounded-lg bg-slate-100 p-1">
              {PERIODS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setPeriod(k)} aria-pressed={period === k} data-testid={`dashboard-approval-period-${k}`}
                  className={`rounded-md px-3 py-1 text-sm transition-colors ${period === k ? 'bg-orange-500 font-semibold text-white shadow-sm' : 'text-slate-600 hover:bg-white hover:text-slate-900'}`}>{l}</button>
              ))}
            </span>
          </span>
        </CardTitle>
        <p className="text-xs text-slate-400">Firmanın talebi İSG'ye gönderdiği an ile onaylandığı an arasındaki süre</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {list.length ? <>
          <div className="flex flex-wrap gap-3">
            <Stat label="Ortalama" value={fmtMinutes(avg)} testid="dashboard-approval-avg" />
            <Stat label="Ortanca" value={fmtMinutes(median)} testid="dashboard-approval-median" />
          </div>
          <HBar data={data} width={70} tip={v => `${v} talep`} colors={BUCKET_COLORS} />
        </> : <Empty icon={Timer} text="Bu seçimde onaylanan talep yok" />}
      </CardContent>
    </Card>
  );
};
